<template>
  <div
    class="selection-opener"
    v-if="hideCombiFilter"
    @click="hideCombiFilter = false"
  >
    <div class="agila-icon-left-sm"></div>
    <div class="selection-opener-text">Zurück zu Ihren Angaben</div>
  </div>

  <div
    class="selection"
    v-if="!hideCombiFilter"
    :class="{
      cat: props.showSpecificPetType === PET_TYPE.CAT,
      dog: props.showSpecificPetType === PET_TYPE.DOG,
      'tks-ops-combi': props.showCombi,
    }"
  >
    <div v-if="props.showCombi" class="selection-headline">
      Bitte wählen Sie die Art des Versicherungschutzes
    </div>
    <div class="selection-pet-type">
      <template v-if="!props.showSpecificPetType && !props.showCombi">
        <agila-radio-buttons
          :options="props.petTypes"
          v-model="selectedPetType"
          icon="ident"
          id="radio-petType"
          ariaLabel="Tierart"
        >
        </agila-radio-buttons>
      </template>
      <div class="selection-text" v-else-if="props.showSpecificPetType">
        Bitte geben Sie Rasse und Geburtstag
        {{
          props.showSpecificPetType === PET_TYPE.DOG
            ? "Ihres Hundes"
            : "Ihrer Katze"
        }}
        an
      </div>
      <div v-else-if="props.showCombi" class="tks-ops-combi-wrapper">
        <agila-radio-images
          id="tksOpsProductGroup"
          v-model="selectedProductGroup"
          class="bg-white"
          :rules="[requiredRule()]"
          :options="[
            {
              id: PRODUCT_GROUP.TKS,
              image: 'agila-icon-tks',
              name: 'Tierkrankenschutz',
            },
            {
              id: PRODUCT_GROUP.OPS,
              image: 'agila-icon-ops',
              name: 'OP-Kostenschutz',
            },
          ]"
          :ref="
            (el) => {
              productGroupTksOpsRef = el;
            }
          "
        ></agila-radio-images>
      </div>
    </div>
    <div class="selection-headline" v-if="props.showCombi">
      Bitte treffen Sie die Angaben zum zu versichernden Hund
    </div>
    <div class="selection-breed-age">
      <div class="selection-breed-age__breed">
        <agila-dropdown-filter
          :rules="[requiredRule()]"
          v-model="selectedBreed"
          :options="selectedPetType?.breeds"
          data-cy="breeds"
          id="breeds"
          label="Rasse"
          :show-label="true"
          scroll-height="40vh"
          :show-error="true"
          :ref="
            (el) => {
              breedCombiRef = el;
            }
          "
        ></agila-dropdown-filter>
      </div>
      <div class="selection-breed-age__age">
        <agila-date-picker
          :rules="[requiredRule()]"
          id="age"
          v-model:selected-date="selectedDate"
          :max-date="maxDate"
          :min-date="minDate"
          label="Geburtstag des Tieres"
          scroll-height="40vh"
          :show-label="true"
          :show-error="true"
          :ref="
            (el) => {
              birthdayCombiRef = el;
            }
          "
        ></agila-date-picker>
      </div>
    </div>
    <agila-button
      v-if="props.showCombi"
      @click="showTariffs()"
      text="Tarife anzeigen"
    >
    </agila-button>
  </div>
</template>

<script setup lang="ts">
import { PET_TYPE, PRODUCT_GROUP } from "../../store/variables";
import { requiredRule } from "frontend-component-templates/src/store/formValidation";
import AgilaDropdownFilter from "frontend-component-templates/src/components/agilaDropdownFilter.vue";
import AgilaButton from "frontend-component-templates/src/components/agilaButton.vue";
import AgilaDatePicker from "frontend-component-templates/src/components/agilaDatePicker.vue";
import AgilaRadioButtons from "frontend-component-templates/src/components/agilaRadioButtons.vue";
import AgilaRadioImages from "frontend-component-templates/src/components/agilaRadioImages.vue";
import { defineProps, withDefaults, defineModel, defineEmits, ref } from "vue";
import { PetType } from "../../classes/petType";

interface Props {
  showCombi?: boolean;
  showSpecificPetType?: number;
  petTypes: PetType[];
}

const props = withDefaults(defineProps<Props>(), {
  showCombi: false,
  showSpecificPetType: null,
  petTypes: null,
});

const emit = defineEmits<{
  (e: "showTariffs", selectedProductGroupId: number): void;
}>();

const selectedPetType = defineModel("selectedPetType");
const selectedBreed = defineModel("selectedBreed");
const selectedDate = defineModel("selectedDate");
const minDate = defineModel("minDate");
const maxDate = defineModel("maxDate");
const hideCombiFilter = defineModel("hideCombiFilter");
const selectedProductGroup = ref(undefined);
const productGroupTksOpsRef = ref(null);
const breedCombiRef = ref(null);
const birthdayCombiRef = ref(null);

async function showTariffs(): void {
  const validBreed = breedCombiRef.value.validate();
  const validBirthday = birthdayCombiRef.value.validate();
  const validProductGroup = props.showCombi
    ? productGroupTksOpsRef.value.validate()
    : true;

  if (validBirthday && validBreed && validProductGroup) {
    emit("showTariffs", selectedProductGroup.value?.id);
  }
}
</script>

<style lang="scss">
@import "../../styles/variables";

.selection-opener {
  cursor: pointer;
  color: var(--color-blue-standard);
  display: flex;
  align-items: center;
  grid-area: a;
  margin: 0 auto;

  @media screen and (min-width: $breakpoint-m) {
    margin-bottom: 12px;
  }

  .selection-opener-text {
    margin-left: 8px;
    text-decoration: underline;
    font-family: var(--font-open-sans-regular), sans-serif;
    font-size: 16px;
    line-height: 24px;
  }
}

.selection {
  grid-area: a;
  display: grid;

  @media screen and (min-width: $breakpoint-m) {
    grid-template-columns: minmax(350px, 34%) 1fr;
    padding-bottom: 12px;
  }

  &.tks-ops-combi {
    background: var(--color-blue-100);
    border-radius: 8px;
    grid-template-columns: 1fr;
    margin: 0 auto 40px;
    width: fit-content;
    padding: 16px;
    max-width: 640px;

    .selection-headline {
      font-size: 20px;
      font-family: var(--font-roboto-bold), sans-serif;
      padding-bottom: 16px;
      text-align: center;

      @media screen and (min-width: $breakpoint-m) {
        text-align: left;
      }
    }

    .selection-breed-age {
      flex-flow: column;
      padding-bottom: 0;

      &__breed {
        padding-bottom: 0;
        max-width: none;
      }
    }

    .selection-pet-type {
      padding: 24px 0;

      .tks-ops-combi-wrapper {
        width: 100%;
        text-align: center;
        gap: 16px;

        .selection-tks,
        .selection-ops {
          background-color: var(--color-white);
          border-radius: 8px;
          padding: 8px;

          label {
            margin-top: 4px;
            display: inline-block;
          }
        }

        .radio-image-con {
          margin-bottom: 0;
        }

        .agila-icon-tks,
        .agila-icon-ops {
          width: 48px;
          height: 48px;
          margin: 0 auto;
        }
        .agila-icon-tks {
          background-image: url("../../../assets/tks-icon.svg");
        }
        .agila-icon-ops {
          background-image: url("../../../assets/ops-icon.svg");
        }
      }
    }

    .selection-pet-type {
      padding: 0 0 12px 0;
    }

    .agila-dropdown-filter,
    .agila-date-picker {
      margin-bottom: 24px;
    }

    .p-button {
      margin-top: 8px;
      justify-content: center;

      &.p-button-sec {
        background: var(--color-blue-100);
      }
    }

    .legal {
      margin-top: 24px;
      font-size: 13px;
      font-family: var(--font-open-sans-regular), sans-serif;
      color: var(--color-gray-500);
    }
  }

  .selection-pet-type {
    display: flex;
    align-items: baseline;
    padding-top: 30px;

    @media screen and (max-width: $breakpoint-m - 1px) {
      justify-content: center;
      text-align: center;
      padding-top: 0;
    }
    @media screen and (max-width: $breakpoint-l - 1px) {
      padding-bottom: 32px;
    }

    > div {
      cursor: pointer;
    }

    .selection-dog {
      margin-right: 32px;
    }

    label {
      padding-left: 9px;
      cursor: pointer;
    }

    .agila-icon-dog,
    .agila-icon-cat {
      vertical-align: bottom;
      margin-left: 8px;
    }

    .agila-radio-buttons {
      margin-bottom: 0;
    }

    .radio-button-cons {
      margin-top: 0;
    }

    .selection-text {
      font-family: var(--font-roboto-bold), sans-serif;
      font-size: 20px;
      padding-right: 16px;
      @media screen and (max-width: 544px) {
        text-align: center;
      }
    }
  }

  .selection-breed-age {
    display: flex;

    @media screen and (max-width: $breakpoint-l - 1px) {
      padding-bottom: 12px;
      flex-wrap: wrap;
    }
    @media screen and (max-width: $breakpoint-m - 1px) {
      flex-wrap: wrap;
      justify-content: center;
      text-align: center;
    }

    &__breed {
      padding-bottom: 12px;
      width: 100%;
      max-width: 492px;

      @media screen and (min-width: $breakpoint-l) {
        padding-bottom: 0;
        margin-right: 32px;
      }
    }

    &__breed,
    &__age {
      label {
        display: block;
      }
    }
  }

  &.cat {
    grid-template-columns: 1fr;
    .selection-pet-type,
    .selection-breed-age {
      justify-content: center;
      text-align: center;
    }

    .selection-pet-type {
      padding-bottom: 24px;
      padding-top: 0;
    }
    .selection-breed-age {
      padding-bottom: 12px;
    }
  }
}
</style>
